import React, { type CSSProperties } from "react";
import {
  ArrowRight,
  Award,
  Calendar,
  CalendarDays,
  Camera,
  CheckCircle2,
  Droplet,
  Home,
  Image,
  MapPin,
  ShieldCheck,
  Star,
  Users,
  Wrench,
  Zap,
} from "lucide-react";
import {
  recentWorkTilesTokens,
  type RecentWorkTilesCategoryTone,
  type RecentWorkTilesColorToken,
  type RecentWorkTilesStatusTone,
} from "./RecentWorkTiles.tokens";

type RecentWorkTilesIconComponent = React.ComponentType<{
  className?: string;
  size?: number;
  style?: CSSProperties;
  strokeWidth?: number;
  "aria-hidden"?: boolean;
}>;

const iconRegistry = {
  arrowRight: ArrowRight,
  award: Award,
  calendar: Calendar,
  calendarDays: CalendarDays,
  camera: Camera,
  checkCircle: CheckCircle2,
  droplet: Droplet,
  home: Home,
  image: Image,
  mapPin: MapPin,
  shieldCheck: ShieldCheck,
  star: Star,
  users: Users,
  wrench: Wrench,
  zap: Zap,
} satisfies Record<string, RecentWorkTilesIconComponent>;

export type RecentWorkTilesIconToken = keyof typeof iconRegistry;

export type RecentWorkTilesLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type RecentWorkTilesImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  originalAlt?: string;
  thumbnailAlt?: string;
  objectPosition?: string;
};

export type RecentWorkTilesCategory = {
  label: React.ReactNode;
  tone?: RecentWorkTilesCategoryTone;
  icon?: React.ReactNode;
  iconToken?: RecentWorkTilesIconToken;
  accentColor?: string;
  accentColour?: string;
  accentSoftColor?: string;
  accentSoftColour?: string;
};

export type RecentWorkTilesMediaBadge = {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  iconToken?: RecentWorkTilesIconToken;
};

export type RecentWorkTilesChip = {
  label: React.ReactNode;
  icon?: React.ReactNode;
  iconToken?: RecentWorkTilesIconToken;
  ariaLabel?: string;
  accentColor?: string;
  backgroundColor?: string;
};

export type RecentWorkTilesTrustItem = {
  id?: string | number;
  label: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  iconToken?: RecentWorkTilesIconToken;
  accentColor?: string;
};

export type RecentWorkTilesItem = {
  id: string | number;
  title: React.ReactNode;
  description?: React.ReactNode;
  href?: string;
  ctaLabel?: React.ReactNode;
  ctaAriaLabel?: string;
  image?: RecentWorkTilesImage;
  imageUrl?: string;
  imageAlt?: string;
  imagePosition?: string;
  media?: RecentWorkTilesMediaBadge;
  mediaLabel?: React.ReactNode;
  mediaIcon?: React.ReactNode;
  mediaIconToken?: RecentWorkTilesIconToken;
  category?: RecentWorkTilesCategory;
  categoryLabel?: React.ReactNode;
  categoryTone?: RecentWorkTilesCategoryTone;
  categoryIcon?: React.ReactNode;
  categoryIconToken?: RecentWorkTilesIconToken;
  location?: React.ReactNode;
  date?: React.ReactNode;
  dateTime?: string;
  statusLabel?: React.ReactNode | null;
  statusTone?: RecentWorkTilesStatusTone;
  statusIcon?: React.ReactNode;
  statusIconToken?: RecentWorkTilesIconToken;
  chips?: RecentWorkTilesChip[];
  accentColor?: string;
  accentColour?: string;
  accentSoftColor?: string;
  accentSoftColour?: string;
  featured?: boolean;
};

export type RecentWorkTilesColors = Partial<Record<RecentWorkTilesColorToken, string>>;

export type RecentWorkTilesProps = {
  items: RecentWorkTilesItem[];
  Link?: RecentWorkTilesLinkComponent;
  eyebrow?: React.ReactNode;
  eyebrowIcon?: React.ReactNode;
  eyebrowIconToken?: RecentWorkTilesIconToken;
  title?: React.ReactNode;
  titleComponent?: "h1" | "h2" | "h3";
  description?: React.ReactNode;
  trustItems?: readonly RecentWorkTilesTrustItem[];
  showTrustItems?: boolean;
  compactOnMobile?: boolean;
  ctaLabel?: React.ReactNode;
  statusLabel?: React.ReactNode;
  mediaLabel?: React.ReactNode;
  colors?: RecentWorkTilesColors;
  colours?: RecentWorkTilesColors;
  accentColor?: string;
  accentColour?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  surfaceColor?: string;
  surfaceColour?: string;
  textColor?: string;
  textColour?: string;
  maxWidth?: number | string;
  imageHeight?: number | string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

type RecentWorkTilesStyle = CSSProperties &
  Record<`--recent-work-tiles-${string}`, string | number | undefined>;

type RecentWorkItemStyle = CSSProperties &
  Record<`--recent-work-tile-${string}`, string | number | undefined>;

const t = recentWorkTilesTokens;

const DefaultLink: RecentWorkTilesLinkComponent = ({ href, children, ...props }) => (
  <a href={href} {...props}>
    {children}
  </a>
);

const recentWorkTilesCss = `
.recent-work-tiles {
  --recent-work-tiles-bg: ${t.colors.background};
  --recent-work-tiles-surface: ${t.colors.surface};
  --recent-work-tiles-text: ${t.colors.text};
  --recent-work-tiles-text-soft: ${t.colors.textSoft};
  --recent-work-tiles-text-muted: ${t.colors.textMuted};
  --recent-work-tiles-accent: ${t.colors.accent};
  --recent-work-tiles-accent-dark: ${t.colors.accentDark};
  --recent-work-tiles-accent-soft: ${t.colors.accentSoft};
  --recent-work-tiles-border: ${t.colors.border};
  --recent-work-tiles-divider: ${t.colors.divider};
  --recent-work-tiles-chip-bg: ${t.colors.chipBackground};
  --recent-work-tiles-image-fallback: ${t.colors.imageFallback};
  --recent-work-tiles-button-text: ${t.colors.buttonText};
  --recent-work-tiles-status-complete: ${t.colors.statusComplete};
  --recent-work-tiles-status-complete-soft: ${t.colors.statusCompleteSoft};
  --recent-work-tiles-shadow: ${t.colors.shadow};
  --recent-work-tiles-hover-shadow: ${t.colors.hoverShadow};
  --recent-work-tiles-max-width: ${t.sizes.maxWidth}px;
  --recent-work-tiles-image-height: ${t.sizes.imageHeight}px;
  width: 100%;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--recent-work-tiles-accent) 10%, transparent) 0, transparent 28%),
    var(--recent-work-tiles-bg);
  color: var(--recent-work-tiles-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.recent-work-tiles,
.recent-work-tiles * {
  box-sizing: border-box;
}
.recent-work-tiles-shell {
  width: 100%;
  max-width: var(--recent-work-tiles-max-width);
  margin: 0 auto;
  padding: ${t.spacing.shellY}px ${t.spacing.shellX}px 54px;
}
.recent-work-tiles-header {
  display: grid;
  justify-items: center;
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}
.recent-work-tiles-eyebrow {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin: 0;
  padding: 8px 18px;
  border-radius: ${t.radius.pill}px;
  background: var(--recent-work-tiles-accent-soft);
  color: var(--recent-work-tiles-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
}
.recent-work-tiles-eyebrow-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.recent-work-tiles-icon,
.recent-work-tiles-icon svg {
  display: block;
  width: var(--recent-work-tiles-icon-size, 16px);
  height: var(--recent-work-tiles-icon-size, 16px);
}
.recent-work-tiles-icon svg {
  stroke-width: 2.4;
}
.recent-work-tiles-title {
  max-width: 100%;
  margin: 18px 0 0;
  color: var(--recent-work-tiles-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  overflow-wrap: anywhere;
}
.recent-work-tiles-description {
  max-width: 760px;
  margin: 20px 0 0;
  color: var(--recent-work-tiles-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
}
.recent-work-tiles-rule {
  width: 92px;
  height: 3px;
  margin-top: 24px;
  border-radius: 999px;
  background: var(--recent-work-tiles-accent);
}
.recent-work-tiles-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${t.spacing.cardGap}px;
  margin-top: 42px;
}
.recent-work-tiles-item {
  display: flex;
  min-width: 0;
}
.recent-work-tiles-card {
  --recent-work-tile-accent: var(--recent-work-tiles-accent);
  --recent-work-tile-accent-soft: var(--recent-work-tiles-accent-soft);
  --recent-work-tile-status: var(--recent-work-tiles-status-complete);
  --recent-work-tile-status-soft: var(--recent-work-tiles-status-complete-soft);
  display: flex;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid var(--recent-work-tiles-border);
  border-radius: ${t.radius.card}px;
  background: var(--recent-work-tiles-surface);
  box-shadow: var(--recent-work-tiles-shadow);
  transition: ${t.motion.card};
}
.recent-work-tiles-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--recent-work-tile-accent) 28%, var(--recent-work-tiles-border));
  box-shadow: var(--recent-work-tiles-hover-shadow);
}
.recent-work-tiles-media {
  position: relative;
  width: 100%;
  min-width: 0;
  height: var(--recent-work-tiles-image-height);
  overflow: hidden;
  background: var(--recent-work-tiles-image-fallback);
}
.recent-work-tiles-image,
.recent-work-tiles-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
}
.recent-work-tiles-image {
  object-fit: cover;
  object-position: var(--recent-work-tiles-image-position, center);
  transform: scale(1);
  transition: ${t.motion.image};
}
.recent-work-tiles-card:hover .recent-work-tiles-image {
  transform: scale(1.045);
  filter: saturate(1.04);
}
.recent-work-tiles-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  color: var(--recent-work-tiles-text-muted);
  font-size: 13px;
  line-height: 1.35;
  font-weight: 750;
  text-align: center;
}
.recent-work-tiles-media-badge {
  position: absolute;
  z-index: 2;
  top: 14px;
  left: 14px;
  display: inline-flex;
  max-width: calc(100% - 28px);
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: ${t.radius.mediaBadge}px;
  background: color-mix(in srgb, var(--recent-work-tiles-accent-dark) 91%, transparent);
  color: #ffffff;
  font-size: ${t.typography.mediaBadge.fontSize}px;
  line-height: ${t.typography.mediaBadge.lineHeight};
  font-weight: ${t.typography.mediaBadge.fontWeight};
  letter-spacing: ${t.typography.mediaBadge.letterSpacing};
  text-transform: ${t.typography.mediaBadge.textTransform};
  box-shadow: 0 10px 22px rgba(7, 23, 53, 0.2);
}
.recent-work-tiles-media-badge-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.recent-work-tiles-content {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  padding: ${t.spacing.cardContentY}px ${t.spacing.cardContentX}px 20px;
}
.recent-work-tiles-card-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}
.recent-work-tiles-category {
  display: inline-flex;
  min-width: 0;
  max-width: 70%;
  align-items: center;
  gap: 7px;
  padding: 7px 11px;
  border: 1px solid color-mix(in srgb, var(--recent-work-tile-accent) 22%, transparent);
  border-radius: ${t.radius.pill}px;
  background: var(--recent-work-tile-accent-soft);
  color: var(--recent-work-tile-accent);
  font-size: ${t.typography.category.fontSize}px;
  line-height: ${t.typography.category.lineHeight};
  font-weight: ${t.typography.category.fontWeight};
  letter-spacing: ${t.typography.category.letterSpacing};
  text-transform: ${t.typography.category.textTransform};
}
.recent-work-tiles-category-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.recent-work-tiles-status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  color: var(--recent-work-tile-status);
  font-size: 12px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
}
.recent-work-tiles-card-title {
  margin: 18px 0 0;
  color: var(--recent-work-tiles-text);
  font-size: ${t.typography.cardTitle.fontSize}px;
  line-height: ${t.typography.cardTitle.lineHeight};
  font-weight: ${t.typography.cardTitle.fontWeight};
  letter-spacing: ${t.typography.cardTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.recent-work-tiles-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 14px;
  color: var(--recent-work-tiles-text-soft);
  font-size: ${t.typography.meta.fontSize}px;
  line-height: ${t.typography.meta.lineHeight};
  font-weight: ${t.typography.meta.fontWeight};
  letter-spacing: ${t.typography.meta.letterSpacing};
}
.recent-work-tiles-meta-item {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
}
.recent-work-tiles-meta-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.recent-work-tiles-meta-divider {
  width: 1px;
  height: 17px;
  background: var(--recent-work-tiles-divider);
}
.recent-work-tiles-card-description {
  margin: 16px 0 0;
  color: var(--recent-work-tiles-text-soft);
  font-size: ${t.typography.body.fontSize}px;
  line-height: ${t.typography.body.lineHeight};
  font-weight: ${t.typography.body.fontWeight};
  letter-spacing: ${t.typography.body.letterSpacing};
}
.recent-work-tiles-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}
.recent-work-tiles-chip {
  --recent-work-tile-chip-accent: var(--recent-work-tile-accent);
  --recent-work-tile-chip-bg: var(--recent-work-tiles-chip-bg);
  display: inline-flex;
  min-height: 34px;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 6px;
  background: var(--recent-work-tile-chip-bg);
  color: var(--recent-work-tiles-text);
  font-size: ${t.typography.chip.fontSize}px;
  line-height: ${t.typography.chip.lineHeight};
  font-weight: ${t.typography.chip.fontWeight};
  letter-spacing: ${t.typography.chip.letterSpacing};
}
.recent-work-tiles-chip-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.recent-work-tiles-chip .recent-work-tiles-icon {
  color: var(--recent-work-tile-chip-accent);
}
.recent-work-tiles-actions {
  display: flex;
  min-width: 0;
  margin-top: auto;
  padding-top: 20px;
}
.recent-work-tiles-cta {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 48px;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: ${t.radius.button}px;
  background: var(--recent-work-tiles-accent-dark);
  color: var(--recent-work-tiles-button-text);
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-align: center;
  text-decoration: none;
  transition: ${t.motion.cta};
}
.recent-work-tiles-cta:hover {
  background: color-mix(in srgb, var(--recent-work-tiles-accent-dark) 86%, var(--recent-work-tile-accent));
  transform: translateY(-1px);
}
.recent-work-tiles-cta:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--recent-work-tile-accent) 28%, transparent);
  outline-offset: 4px;
}
.recent-work-tiles-cta-label {
  min-width: 0;
  overflow-wrap: anywhere;
}
.recent-work-tiles-cta .recent-work-tiles-icon {
  justify-self: end;
}
.recent-work-tiles-trust {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  margin-top: 34px;
  overflow: hidden;
  border: 1px solid var(--recent-work-tiles-border);
  border-radius: ${t.radius.card}px;
  background: color-mix(in srgb, var(--recent-work-tiles-surface) 82%, transparent);
  box-shadow: 0 14px 34px rgba(7, 23, 53, 0.06);
}
.recent-work-tiles-trust-item {
  display: grid;
  min-width: 0;
  grid-template-columns: ${t.sizes.trustIcon}px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
}
.recent-work-tiles-trust-item + .recent-work-tiles-trust-item {
  border-left: 1px solid var(--recent-work-tiles-divider);
}
.recent-work-tiles-trust-icon {
  display: inline-flex;
  width: ${t.sizes.trustIcon}px;
  height: ${t.sizes.trustIcon}px;
  align-items: center;
  justify-content: center;
  color: var(--recent-work-tile-trust-accent, var(--recent-work-tiles-accent));
}
.recent-work-tiles-trust-icon .recent-work-tiles-icon,
.recent-work-tiles-trust-icon .recent-work-tiles-icon svg {
  width: 42px;
  height: 42px;
}
.recent-work-tiles-trust-copy {
  min-width: 0;
}
.recent-work-tiles-trust-title {
  display: block;
  margin: 0;
  color: var(--recent-work-tiles-text);
  font-size: ${t.typography.trustTitle.fontSize}px;
  line-height: ${t.typography.trustTitle.lineHeight};
  font-weight: ${t.typography.trustTitle.fontWeight};
  letter-spacing: ${t.typography.trustTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.recent-work-tiles-trust-description {
  display: block;
  margin: 6px 0 0;
  color: var(--recent-work-tiles-text-muted);
  font-size: ${t.typography.trustText.fontSize}px;
  line-height: ${t.typography.trustText.lineHeight};
  font-weight: ${t.typography.trustText.fontWeight};
  letter-spacing: ${t.typography.trustText.letterSpacing};
  overflow-wrap: anywhere;
}
@container (max-width: 1079px) {
  .recent-work-tiles-shell {
    padding: ${t.spacing.tabletShellY}px ${t.spacing.tabletShellX}px 42px;
  }
  .recent-work-tiles-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .recent-work-tiles-description {
    font-size: ${t.typography.description.tabletFontSize}px;
  }
  .recent-work-tiles-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${t.spacing.tabletCardGap}px;
  }
  .recent-work-tiles-item[data-featured="true"] {
    grid-column: 1 / -1;
  }
  .recent-work-tiles-card[data-featured="true"] {
    display: grid;
    grid-template-columns: minmax(260px, 0.82fr) minmax(0, 1.18fr);
  }
  .recent-work-tiles-card[data-featured="true"] .recent-work-tiles-media {
    height: 100%;
    min-height: ${t.sizes.tabletImageHeight}px;
  }
  .recent-work-tiles-card[data-featured="true"] .recent-work-tiles-content {
    padding: 22px 24px 22px;
  }
  .recent-work-tiles-card-title {
    font-size: ${t.typography.cardTitle.tabletFontSize}px;
  }
  .recent-work-tiles-trust {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .recent-work-tiles-trust-item + .recent-work-tiles-trust-item {
    border-left: 0;
  }
  .recent-work-tiles-trust-item:nth-child(even) {
    border-left: 1px solid var(--recent-work-tiles-divider);
  }
  .recent-work-tiles-trust-item:nth-child(n + 3) {
    border-top: 1px solid var(--recent-work-tiles-divider);
  }
}
@container (max-width: 767px) {
  .recent-work-tiles-shell {
    padding: ${t.spacing.mobileShellY}px ${t.spacing.mobileShellX}px 30px;
  }
  .recent-work-tiles-eyebrow {
    gap: 7px;
    padding: 7px 13px;
    font-size: ${t.typography.eyebrow.mobileFontSize}px;
  }
  .recent-work-tiles-title {
    margin-top: 15px;
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: ${t.typography.title.mobileLineHeight};
  }
  .recent-work-tiles-description {
    margin-top: 12px;
    font-size: ${t.typography.description.mobileFontSize}px;
  }
  .recent-work-tiles-rule {
    width: 56px;
    height: 2px;
    margin-top: 15px;
  }
  .recent-work-tiles-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: ${t.spacing.mobileCardGap}px;
    margin-top: 28px;
  }
  .recent-work-tiles-card[data-featured="true"] {
    display: flex;
  }
  .recent-work-tiles-media,
  .recent-work-tiles-card[data-featured="true"] .recent-work-tiles-media {
    height: ${t.sizes.mobileImageHeight}px;
    min-height: 0;
  }
  .recent-work-tiles-media-badge {
    top: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
    padding: 7px 9px;
    font-size: ${t.typography.mediaBadge.mobileFontSize}px;
  }
  .recent-work-tiles-content,
  .recent-work-tiles-card[data-featured="true"] .recent-work-tiles-content {
    padding: 14px 12px 12px;
  }
  .recent-work-tiles-card-topline {
    gap: 8px;
  }
  .recent-work-tiles-category {
    max-width: 66%;
    gap: 5px;
    padding: 6px 9px;
    font-size: ${t.typography.category.mobileFontSize}px;
  }
  .recent-work-tiles-status {
    gap: 5px;
    font-size: 10px;
  }
  .recent-work-tiles-card-title {
    margin-top: 12px;
    font-size: ${t.typography.cardTitle.mobileFontSize}px;
    line-height: 1.18;
  }
  .recent-work-tiles-meta {
    gap: 6px 9px;
    margin-top: 9px;
    font-size: ${t.typography.meta.mobileFontSize}px;
  }
  .recent-work-tiles-meta-divider {
    height: 13px;
  }
  .recent-work-tiles[data-compact-mobile="true"] .recent-work-tiles-card-description {
    display: none;
  }
  .recent-work-tiles-card-description {
    margin-top: 12px;
    font-size: 12px;
    line-height: 1.45;
  }
  .recent-work-tiles-chips {
    gap: 8px;
    margin-top: 13px;
  }
  .recent-work-tiles-chip {
    min-height: 28px;
    gap: 6px;
    padding: 0 9px;
    font-size: ${t.typography.chip.mobileFontSize}px;
  }
  .recent-work-tiles-actions {
    padding-top: 14px;
  }
  .recent-work-tiles-cta {
    min-height: 38px;
    padding: 10px 12px;
    font-size: ${t.typography.cta.mobileFontSize}px;
  }
  .recent-work-tiles-trust {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 16px;
  }
  .recent-work-tiles-trust-item,
  .recent-work-tiles-trust-item:nth-child(even),
  .recent-work-tiles-trust-item:nth-child(n + 3) {
    grid-template-columns: ${t.sizes.mobileTrustIcon}px minmax(0, 1fr);
    gap: 12px;
    padding: 11px 12px;
    border-left: 0;
    border-top: 1px solid var(--recent-work-tiles-divider);
  }
  .recent-work-tiles-trust-item:first-child {
    border-top: 0;
  }
  .recent-work-tiles-trust-icon {
    width: ${t.sizes.mobileTrustIcon}px;
    height: ${t.sizes.mobileTrustIcon}px;
  }
  .recent-work-tiles-trust-icon .recent-work-tiles-icon,
  .recent-work-tiles-trust-icon .recent-work-tiles-icon svg {
    width: 30px;
    height: 30px;
  }
  .recent-work-tiles-trust-title {
    font-size: ${t.typography.trustTitle.mobileFontSize}px;
  }
  .recent-work-tiles-trust-description {
    margin-top: 3px;
    font-size: ${t.typography.trustText.mobileFontSize}px;
  }
  .recent-work-tiles-icon,
  .recent-work-tiles-icon svg {
    width: var(--recent-work-tiles-icon-size, 14px);
    height: var(--recent-work-tiles-icon-size, 14px);
  }
}
@container (max-width: 380px) {
  .recent-work-tiles-shell {
    padding-left: 12px;
    padding-right: 12px;
  }
  .recent-work-tiles-title {
    font-size: 29px;
  }
  .recent-work-tiles-category {
    max-width: 62%;
  }
  .recent-work-tiles-chip {
    max-width: 100%;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function normalizeTone(value?: React.ReactNode): RecentWorkTilesCategoryTone | undefined {
  if (typeof value !== "string") return undefined;

  const key = value.toLowerCase().replace(/[^a-z0-9]+/g, "") as RecentWorkTilesCategoryTone;

  if (key in t.categoryPalettes) {
    return key;
  }

  return undefined;
}

function resolveIconToken(token?: RecentWorkTilesIconToken): RecentWorkTilesIconToken | undefined {
  if (!token || !(token in iconRegistry)) return undefined;
  return token;
}

function RecentWorkTilesIcon({
  icon,
  token,
  fallbackToken,
  size = 16,
}: {
  icon?: React.ReactNode;
  token?: RecentWorkTilesIconToken;
  fallbackToken?: RecentWorkTilesIconToken;
  size?: number;
}) {
  if (icon) {
    return (
      <span
        className="recent-work-tiles-icon"
        style={{ "--recent-work-tiles-icon-size": `${size}px` } as RecentWorkTilesStyle}
        aria-hidden="true"
      >
        {icon}
      </span>
    );
  }

  const resolvedToken = resolveIconToken(token) || resolveIconToken(fallbackToken);
  const Icon = resolvedToken ? iconRegistry[resolvedToken] : undefined;

  if (!Icon) return null;

  return (
    <Icon
      className="recent-work-tiles-icon"
      size={size}
      strokeWidth={2.4}
      aria-hidden="true"
      style={{ "--recent-work-tiles-icon-size": `${size}px` } as CSSProperties}
    />
  );
}

function getImage(item: RecentWorkTilesItem): RecentWorkTilesImage {
  if (item.image) return item.image;

  if (item.imageUrl) {
    return {
      src: item.imageUrl,
      alt: item.imageAlt,
      objectPosition: item.imagePosition,
    };
  }

  return {};
}

function getImageSrc(image: RecentWorkTilesImage): string {
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: RecentWorkTilesImage, title: React.ReactNode): string {
  if (image.alt || image.thumbnailAlt || image.originalAlt || image.title) {
    return image.alt || image.thumbnailAlt || image.originalAlt || image.title || "";
  }

  return typeof title === "string" ? title : "Recent work image";
}

function getCategory(item: RecentWorkTilesItem) {
  const label = item.category?.label || item.categoryLabel;
  const tone = item.category?.tone || item.categoryTone || normalizeTone(label);
  const palette = tone ? t.categoryPalettes[tone] : undefined;
  const accent =
    item.accentColor ||
    item.accentColour ||
    item.category?.accentColor ||
    item.category?.accentColour ||
    palette?.accent ||
    t.colors.accent;
  const soft =
    item.accentSoftColor ||
    item.accentSoftColour ||
    item.category?.accentSoftColor ||
    item.category?.accentSoftColour ||
    palette?.soft ||
    t.colors.accentSoft;
  const token =
    item.category?.iconToken ||
    item.categoryIconToken ||
    (tone === "electrical"
      ? (t.icons.electrical as RecentWorkTilesIconToken)
      : tone === "plumbing"
        ? (t.icons.plumbing as RecentWorkTilesIconToken)
        : tone === "roofing"
          ? (t.icons.roofing as RecentWorkTilesIconToken)
          : (t.icons.categoryDefault as RecentWorkTilesIconToken));

  return {
    label,
    accent,
    soft,
    icon: item.category?.icon || item.categoryIcon,
    iconToken: token,
  };
}

function getStatusTone(tone: RecentWorkTilesStatusTone | undefined) {
  if (tone && tone in t.statusTones) return t.statusTones[tone];
  return t.statusTones.complete;
}

function getMetaItems(item: RecentWorkTilesItem) {
  return [
    item.location
      ? {
          id: "location",
          iconToken: t.icons.location as RecentWorkTilesIconToken,
          value: item.location,
        }
      : undefined,
    item.date
      ? {
          id: "date",
          iconToken: t.icons.date as RecentWorkTilesIconToken,
          value: item.date,
          dateTime: item.dateTime,
        }
      : undefined,
  ].filter(Boolean) as {
    id: string;
    iconToken: RecentWorkTilesIconToken;
    value: React.ReactNode;
    dateTime?: string;
  }[];
}

function getColors({
  colors,
  colours,
  accentColor,
  accentColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
}: Pick<
  RecentWorkTilesProps,
  | "colors"
  | "colours"
  | "accentColor"
  | "accentColour"
  | "backgroundColor"
  | "backgroundColour"
  | "surfaceColor"
  | "surfaceColour"
  | "textColor"
  | "textColour"
>) {
  const resolved = {
    ...t.colors,
    ...colours,
    ...colors,
  };

  return {
    ...resolved,
    accent: accentColor || accentColour || resolved.accent,
    background: backgroundColor || backgroundColour || resolved.background,
    surface: surfaceColor || surfaceColour || resolved.surface,
    text: textColor || textColour || resolved.text,
  };
}

export default function RecentWorkTiles({
  items,
  Link = DefaultLink,
  eyebrow = t.defaults.eyebrow,
  eyebrowIcon,
  eyebrowIconToken = t.icons.eyebrow as RecentWorkTilesIconToken,
  title = t.defaults.title,
  titleComponent = "h2",
  description = t.defaults.description,
  trustItems = t.defaults.trustItems,
  showTrustItems = true,
  compactOnMobile = true,
  ctaLabel = t.defaults.ctaLabel,
  statusLabel = t.defaults.statusLabel,
  mediaLabel = t.defaults.mediaLabel,
  colors,
  colours,
  accentColor,
  accentColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
  maxWidth = t.sizes.maxWidth,
  imageHeight = t.sizes.imageHeight,
  className = "",
  style,
  ariaLabel,
}: RecentWorkTilesProps) {
  if (!items.length) return null;

  const resolvedColors = getColors({
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
  });
  const TitleTag = titleComponent;

  return (
    <section
      className={`recent-work-tiles ${className}`}
      data-compact-mobile={compactOnMobile ? "true" : "false"}
      aria-label={ariaLabel || (typeof title === "string" ? title : "Recent work")}
      style={
        {
          "--recent-work-tiles-bg": resolvedColors.background,
          "--recent-work-tiles-surface": resolvedColors.surface,
          "--recent-work-tiles-text": resolvedColors.text,
          "--recent-work-tiles-text-soft": resolvedColors.textSoft,
          "--recent-work-tiles-text-muted": resolvedColors.textMuted,
          "--recent-work-tiles-accent": resolvedColors.accent,
          "--recent-work-tiles-accent-dark": resolvedColors.accentDark,
          "--recent-work-tiles-accent-soft": resolvedColors.accentSoft,
          "--recent-work-tiles-border": resolvedColors.border,
          "--recent-work-tiles-divider": resolvedColors.divider,
          "--recent-work-tiles-chip-bg": resolvedColors.chipBackground,
          "--recent-work-tiles-image-fallback": resolvedColors.imageFallback,
          "--recent-work-tiles-button-text": resolvedColors.buttonText,
          "--recent-work-tiles-status-complete": resolvedColors.statusComplete,
          "--recent-work-tiles-status-complete-soft": resolvedColors.statusCompleteSoft,
          "--recent-work-tiles-shadow": resolvedColors.shadow,
          "--recent-work-tiles-hover-shadow": resolvedColors.hoverShadow,
          "--recent-work-tiles-max-width": getCssSize(maxWidth),
          "--recent-work-tiles-image-height": getCssSize(imageHeight),
          ...style,
        } as RecentWorkTilesStyle
      }
    >
      <style>{recentWorkTilesCss}</style>

      <div className="recent-work-tiles-shell">
        {eyebrow || title || description ? (
          <div className="recent-work-tiles-header">
            {eyebrow ? (
              <p className="recent-work-tiles-eyebrow">
                <RecentWorkTilesIcon icon={eyebrowIcon} token={eyebrowIconToken} size={15} />
                <span className="recent-work-tiles-eyebrow-text">{eyebrow}</span>
              </p>
            ) : null}

            {title ? <TitleTag className="recent-work-tiles-title">{title}</TitleTag> : null}

            {description ? <p className="recent-work-tiles-description">{description}</p> : null}

            <span className="recent-work-tiles-rule" aria-hidden="true" />
          </div>
        ) : null}

        <div className="recent-work-tiles-grid">
          {items.map((item) => {
            const image = getImage(item);
            const imageSrc = getImageSrc(image);
            const imagePosition = item.imagePosition || image.objectPosition || "center";
            const imageAlt = getImageAlt(image, item.title);
            const category = getCategory(item);
            const statusTone = getStatusTone(item.statusTone);
            const metaItems = getMetaItems(item);
            const resolvedStatusLabel = item.statusLabel === null ? null : item.statusLabel || statusLabel;
            const resolvedMediaLabel = item.media?.label || item.mediaLabel || mediaLabel;
            const resolvedMediaIcon =
              item.media?.icon || item.mediaIcon;
            const resolvedMediaIconToken =
              item.media?.iconToken ||
              item.mediaIconToken ||
              (resolvedMediaLabel === "Before & After"
                ? (t.icons.beforeAfter as RecentWorkTilesIconToken)
                : (t.icons.media as RecentWorkTilesIconToken));

            return (
              <article
                key={item.id}
                className="recent-work-tiles-item"
                data-featured={item.featured ? "true" : "false"}
              >
                <div
                  className="recent-work-tiles-card"
                  data-featured={item.featured ? "true" : "false"}
                  style={
                    {
                      "--recent-work-tile-accent": category.accent,
                      "--recent-work-tile-accent-soft": category.soft,
                      "--recent-work-tile-status": statusTone.color,
                      "--recent-work-tile-status-soft": statusTone.soft,
                    } as RecentWorkItemStyle
                  }
                >
                  <div className="recent-work-tiles-media">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={imageAlt}
                        className="recent-work-tiles-image"
                        loading="lazy"
                        style={{ "--recent-work-tiles-image-position": imagePosition } as RecentWorkTilesStyle}
                      />
                    ) : (
                      <div className="recent-work-tiles-image-fallback">{item.title}</div>
                    )}

                    {resolvedMediaLabel ? (
                      <span className="recent-work-tiles-media-badge">
                        <RecentWorkTilesIcon
                          icon={resolvedMediaIcon}
                          token={resolvedMediaIconToken}
                          size={15}
                        />
                        <span className="recent-work-tiles-media-badge-text">{resolvedMediaLabel}</span>
                      </span>
                    ) : null}
                  </div>

                  <div className="recent-work-tiles-content">
                    <div className="recent-work-tiles-card-topline">
                      {category.label ? (
                        <span className="recent-work-tiles-category">
                          <RecentWorkTilesIcon
                            icon={category.icon}
                            token={category.iconToken}
                            size={14}
                          />
                          <span className="recent-work-tiles-category-text">{category.label}</span>
                        </span>
                      ) : null}

                      {resolvedStatusLabel ? (
                        <span className="recent-work-tiles-status">
                          <RecentWorkTilesIcon
                            icon={item.statusIcon}
                            token={item.statusIconToken || (t.icons.status as RecentWorkTilesIconToken)}
                            size={14}
                          />
                          <span>{resolvedStatusLabel}</span>
                        </span>
                      ) : null}
                    </div>

                    <h3 className="recent-work-tiles-card-title">{item.title}</h3>

                    {metaItems.length ? (
                      <div className="recent-work-tiles-meta">
                        {metaItems.map((meta, index) => (
                          <React.Fragment key={meta.id}>
                            {index > 0 ? <span className="recent-work-tiles-meta-divider" aria-hidden="true" /> : null}
                            <span className="recent-work-tiles-meta-item">
                              <RecentWorkTilesIcon token={meta.iconToken} size={14} />
                              {meta.dateTime ? (
                                <time dateTime={meta.dateTime} className="recent-work-tiles-meta-text">
                                  {meta.value}
                                </time>
                              ) : (
                                <span className="recent-work-tiles-meta-text">{meta.value}</span>
                              )}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    ) : null}

                    {item.description ? (
                      <p className="recent-work-tiles-card-description">{item.description}</p>
                    ) : null}

                    {item.chips?.length ? (
                      <div className="recent-work-tiles-chips">
                        {item.chips.map((chip, index) => (
                          <span
                            key={index}
                            className="recent-work-tiles-chip"
                            aria-label={chip.ariaLabel}
                            style={
                              {
                                "--recent-work-tile-chip-accent": chip.accentColor || category.accent,
                                "--recent-work-tile-chip-bg": chip.backgroundColor || resolvedColors.chipBackground,
                              } as RecentWorkItemStyle
                            }
                          >
                            <RecentWorkTilesIcon icon={chip.icon} token={chip.iconToken} size={15} />
                            <span className="recent-work-tiles-chip-text">{chip.label}</span>
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {item.href ? (
                      <div className="recent-work-tiles-actions">
                        <Link
                          href={item.href}
                          className="recent-work-tiles-cta"
                          title={typeof item.title === "string" ? item.title : undefined}
                          aria-label={item.ctaAriaLabel || `View job: ${typeof item.title === "string" ? item.title : "recent work"}`}
                        >
                          <span className="recent-work-tiles-cta-label">{item.ctaLabel || ctaLabel}</span>
                          <RecentWorkTilesIcon token={t.icons.cta as RecentWorkTilesIconToken} size={18} />
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {showTrustItems && trustItems.length ? (
          <div className="recent-work-tiles-trust" aria-label="Work guarantees">
            {trustItems.map((trustItem, index) => (
              <div
                key={trustItem.id || index}
                className="recent-work-tiles-trust-item"
                style={{ "--recent-work-tile-trust-accent": trustItem.accentColor || resolvedColors.accent } as RecentWorkItemStyle}
              >
                <span className="recent-work-tiles-trust-icon" aria-hidden="true">
                  <RecentWorkTilesIcon
                    icon={trustItem.icon}
                    token={trustItem.iconToken || (t.defaults.trustItems[index]?.iconToken as RecentWorkTilesIconToken)}
                    size={40}
                  />
                </span>
                <span className="recent-work-tiles-trust-copy">
                  <strong className="recent-work-tiles-trust-title">{trustItem.label}</strong>
                  {trustItem.description ? (
                    <span className="recent-work-tiles-trust-description">{trustItem.description}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export { recentWorkTilesTokens };
